/**
 * tm/components/GovWatchAlerts.tsx — 대시보드 「기다리던 공고가 나왔어요」 (2026-10-30 · 공고 나오면 알림)
 *  확인 안 한 알림만 · 기다림 하나 = 한 줄(같은 공고가 여러 출처로 들어와도 「외 n건」) · 없으면 아무것도 안 그림
 *  단추(같은 폭): 공고 보기 · 업무 만들기(업체 업무 탭 · 업무 등록 열림 · 저장하면 알림 끝) · 확인
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import * as api from '../api';
import type { GovWatchAlert } from '../api';
import { C, T, R } from './ui';
import { smsLine } from './GovWatchSection';

const W = 88;
const btn = (primary = false): CSSProperties => ({
  width: W, height: 28, borderRadius: R.sm, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
  border: `1px solid ${primary ? C.green : C.border}`, background: primary ? C.green : C.white, color: primary ? C.white : C.sub,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0,
});

interface Props { onNavigate: (section: string, params?: unknown) => void; onToast?: (msg: string, type?: 'success' | 'error') => void }

export default function GovWatchAlerts({ onNavigate, onToast }: Props) {
  const [items, setItems] = useState<GovWatchAlert[]>([]);
  const load = useCallback(() => { api.getGovWatchAlerts().then(r => setItems((r.data && r.data.items) || [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);
  if (!items.length) return null;

  async function seen(a: GovWatchAlert) {
    try { await api.seenGovWatch(a.watch_id); setItems(xs => xs.filter(x => x.watch_id !== a.watch_id)); }
    catch (e: unknown) { if (onToast) onToast(e instanceof Error ? e.message : '저장하지 못했어요', 'error'); }
  }
  return (
    <div style={{ marginBottom: 12, borderRadius: R.md, border: `1px solid ${C.green}`, background: C.white, overflow: 'hidden' }}>
      <div style={{ padding: '9px 14px', background: C.greenLt, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '2px 8px' }}>
        <b style={{ fontSize: T.md, color: '#166534', whiteSpace: 'nowrap' }}>📣 기다리던 공고가 나왔어요 {items.length}건</b>
        <span style={{ fontSize: T.sm, color: '#166534' }}>신청하기로 한 사업이에요</span>
      </div>
      {items.map((a, i) => {
        const s = smsLine(a.sms);
        return (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: i ? `1px solid ${C.border}` : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 280px', minWidth: 0 }}>
              <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>{a.company_name || '업체'} · {a.watch_name}{a.memo ? <span style={{ fontWeight: 400, color: C.sub }}> · {a.memo}</span> : null}</div>
              <div style={{ fontSize: T.sm, color: C.text, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}{a.more ? <span style={{ color: C.muted }}> 외 {a.more}건</span> : null}</div>
              <div style={{ fontSize: T.xs, color: C.sub, marginTop: 2 }}>접수 {a.period}{a.agency ? ` · ${a.agency}` : ''}{s.text ? <span style={{ color: s.tone === 'bad' ? C.red : C.sub }}> · {s.text}</span> : null}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {a.url ? <a href={a.url} target="_blank" rel="noopener noreferrer" style={btn()}>공고 보기</a> : <span style={{ ...btn(), color: C.muted, cursor: 'default' }}>주소 없음</span>}
              <button type="button" onClick={() => onNavigate('companies', { companyId: a.company_id, watch: { id: a.watch_id, name: a.watch_name } })} style={btn(true)}>업무 만들기</button>
              <button type="button" onClick={() => void seen(a)} title="이 알림을 목록에서 내려요 — 업체 업무 탭 「기다리는 공고」에는 남아요" style={btn()}>확인</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
